import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield, Plus, X, Trash2, LibraryBig, Search, Check, Sparkles, Bot, FlaskConical, Loader2, AlertTriangle, Tag, ScanSearch } from 'lucide-react';
import { getLlmGuardrail, setLlmGuardrailConfig, addLlmGuardrailPolicy, deleteLlmGuardrailPolicy, testLlmGuardrail,
  getPiiGuardrail, setPiiGuardrailConfig, addPiiPattern, deletePiiPattern, testPiiGuardrail } from '../api';
import { GUARDRAIL_PRESETS, GUARDRAIL_CATEGORIES, RECOMMENDED_PRESET_IDS } from '../constants/guardrailPresets';

const RISK_DOT = { low: 'bg-green-400', med: 'bg-amber-400', high: 'bg-red-400' };
const RISK_LABEL = { low: 'low false-positive risk', med: 'medium false-positive risk', high: 'high false-positive risk' };

// Category tag → chip colour (tags come from the [PII]/[FIN]/… name prefix).
const TAG_STYLE = {
  PII: 'bg-blue-500/10 text-blue-400',
  PHI: 'bg-teal-500/10 text-teal-400',
  FIN: 'bg-emerald-500/10 text-emerald-400',
  SECRET: 'bg-amber-500/10 text-amber-400',
  SEC: 'bg-purple-500/10 text-purple-400',
  EXFIL: 'bg-cyan-500/10 text-cyan-400',
  REG: 'bg-rose-500/10 text-rose-400',
  SAFETY: 'bg-pink-500/10 text-pink-400',
};

function parseName(name) {
  const m = /^\[(\w+)\]\s*(.*)$/.exec(name || '');
  return m ? { tag: m[1], label: m[2] } : { tag: null, label: name };
}

function Toggle({ on, onClick }) {
  return (
    <button onClick={onClick}
      className={`relative w-14 h-7 shrink-0 rounded-full transition-colors ${on ? 'bg-orange' : 'bg-field border border-field-border'}`}>
      <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${on ? 'left-7' : 'left-0.5'}`} />
    </button>
  );
}

// Library of curated regex presets — adds them as Layer 3 custom patterns.
function LibraryModal({ existingPatterns, onClose, onAdd, onBulkAdded }) {
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return GUARDRAIL_PRESETS;
    return GUARDRAIL_PRESETS.filter((p) =>
      `${p.name} ${p.description} ${p.compliance} ${p.savedName}`.toLowerCase().includes(needle));
  }, [q]);

  const toggle = (id, added) => {
    if (added) return;
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bulkAdd = async (ids) => {
    const presets = GUARDRAIL_PRESETS.filter(
      (p) => ids.includes(p.id) && !existingPatterns.has(p.pattern));
    if (!presets.length) return;
    setBusy(true);
    try {
      for (const p of presets) {
        await onAdd(p);
      }
      onBulkAdded(presets.length);
    } finally {
      setBusy(false);
    }
  };

  const addSelected = () => bulkAdd([...selected]);
  const addRecommended = () => bulkAdd(RECOMMENDED_PRESET_IDS);

  const recoToAdd = RECOMMENDED_PRESET_IDS.filter((id) => {
    const p = GUARDRAIL_PRESETS.find((x) => x.id === id);
    return p && !existingPatterns.has(p.pattern);
  }).length;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-panel border border-panel-border rounded-2xl w-full max-w-3xl max-h-[88vh] flex flex-col shadow-2xl">
        {/* header */}
        <div className="flex justify-between items-start p-6 pb-4 border-b border-panel-border">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange/10 flex items-center justify-center shrink-0">
              <LibraryBig className="w-5 h-5 text-orange" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-fg">Guardrail library</h2>
              <p className="text-fg-muted text-xs mt-0.5">{GUARDRAIL_PRESETS.length} curated patterns mapped to PCI-DSS · DPDP · RBI/SEBI · OWASP</p>
            </div>
          </div>
          <button onClick={onClose} className="text-fg-muted hover:text-fg p-1 rounded-lg hover:bg-field"><X className="w-5 h-5" /></button>
        </div>

        {/* search */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2 px-3 py-2 bg-field border border-field-border rounded-lg focus-within:border-orange">
            <Search className="w-4 h-4 text-fg-muted shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="Search by name or compliance tag (PCI, DPDP, SEBI)…"
              className="w-full bg-transparent text-fg text-sm outline-none border-0 focus:shadow-none placeholder:text-fg-muted/60" />
          </div>
        </div>

        {/* scroll body */}
        <div className="overflow-y-auto px-6 py-4 space-y-6">
          {/* starter-pack banner */}
          {!q && (
            <div className="rounded-xl border border-orange/30 bg-orange/5 p-4 flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-orange shrink-0 mt-0.5" />
                <div>
                  <div className="text-fg font-medium text-sm">Recommended starter pack</div>
                  <div className="text-fg-muted text-xs mt-0.5 max-w-md">
                    One click adds the {RECOMMENDED_PRESET_IDS.length} low-false-positive guardrails (PAN, Aadhaar, cards, keys…), enabled and safe for day-one.
                  </div>
                </div>
              </div>
              <button onClick={addRecommended} disabled={busy || recoToAdd === 0}
                className="px-3.5 py-2 bg-orange hover:bg-orange-hover text-white text-sm font-medium rounded-lg whitespace-nowrap disabled:opacity-50 transition-colors">
                {recoToAdd === 0 ? 'All added' : `Add ${recoToAdd}`}
              </button>
            </div>
          )}

          {GUARDRAIL_CATEGORIES.map((cat) => {
            const items = filtered.filter((p) => p.category === cat.key);
            if (!items.length) return null;
            return (
              <div key={cat.key}>
                <div className="flex items-center gap-2 mb-2.5">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${TAG_STYLE[cat.tag] || 'bg-field text-fg-muted'}`}>{cat.tag}</span>
                  <span className="text-xs font-medium text-fg">{cat.label}</span>
                  <span className="text-xs text-fg-muted">· {items.length}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {items.map((p) => {
                    const added = existingPatterns.has(p.pattern);
                    const sel = selected.has(p.id);
                    return (
                      <div key={p.id} onClick={() => toggle(p.id, added)}
                        className={`group relative border rounded-xl p-3 transition-all ${
                          added ? 'border-panel-border opacity-55 cursor-default'
                            : sel ? 'border-orange bg-orange/[0.06] cursor-pointer'
                              : 'border-panel-border hover:border-orange/50 hover:bg-field/40 cursor-pointer'}`}>
                        <div className="flex items-start gap-2.5">
                          {/* checkbox */}
                          <span className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                            added ? 'bg-green-500/20 border-green-500/40' : sel ? 'bg-orange border-orange' : 'border-field-border group-hover:border-orange/60'}`}>
                            {(sel || added) && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${RISK_DOT[p.risk]}`} title={RISK_LABEL[p.risk]} />
                              <span className="text-fg text-sm font-medium truncate">{p.name}</span>
                            </div>
                            <p className="text-fg-muted text-xs mt-1 line-clamp-2 leading-relaxed">{p.description}</p>
                            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                              <span className="px-1.5 py-0.5 rounded bg-field text-fg-muted text-[10px]">{p.compliance}</span>
                              {p.recommended && <span className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 text-[10px]">recommended</span>}
                              {added && <span className="text-green-400 text-[10px] ml-auto">Added</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {!filtered.length && <div className="text-center text-fg-muted py-12 text-sm">No presets match “{q}”.</div>}
        </div>

        {/* footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-panel-border">
          <span className="text-fg-muted text-xs">{selected.size ? `${selected.size} selected` : 'Tick cards to select, then add'}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-field border border-field-border text-fg rounded-lg hover:border-orange text-sm transition-colors">Close</button>
            <button onClick={addSelected} disabled={busy || !selected.size}
              className="px-4 py-2 bg-orange hover:bg-orange-hover text-white font-medium rounded-lg text-sm disabled:opacity-50 transition-colors">
              {busy ? 'Adding…' : `Add ${selected.size || ''} selected`.trim()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── L2: LLM-based guardrail ─────────────────────────────────────────────────
function AddPolicyModal({ onClose, onAdd }) {
  const [label, setLabel] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    if (!text.trim()) { setError('Instruction is required'); return; }
    setBusy(true); setError(null);
    try { await onAdd({ label: label.trim(), text: text.trim() }); }
    catch (e) { setError(e.response?.data?.detail || 'Failed to add'); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-panel border border-panel-border rounded-2xl p-6 w-full max-w-lg shadow-2xl">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-fg">Add policy</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg p-1 rounded-lg hover:bg-field"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1.5">Label <span className="text-fg-muted/60">(short tag)</span></label>
            <input value={label} onChange={(e) => setLabel(e.target.value)}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm" placeholder="e.g. Block PII" />
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1.5">Instruction to the judge model</label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm resize-y"
              placeholder="Block any message that contains personally identifiable information — names, SSN, Aadhaar, addresses, phone numbers." />
          </div>
          {error && <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}
        </div>
        <div className="flex gap-3 pt-5">
          <button onClick={submit} disabled={busy} className="flex-1 py-2.5 bg-orange hover:bg-orange-hover text-white font-medium rounded-lg text-sm disabled:opacity-50 transition-colors">
            {busy ? 'Adding…' : 'Add policy'}
          </button>
          <button onClick={onClose} className="px-6 py-2.5 bg-field border border-field-border text-fg rounded-lg hover:border-orange text-sm transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function LlmGuardrailSection({ flash, onEnabled }) {
  const [cfg, setCfg] = useState(null);              // {enabled, model, provider, policies, fail_mode, block_message, timeout_seconds}
  const [models, setModels] = useState([]);          // [{id, provider}]
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);
  const [adding, setAdding] = useState(false);
  const [testText, setTestText] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getLlmGuardrail();
      setCfg(r.data.config);
      setModels(r.data.available_models || []);
    } catch { /* surfaced by parent refresh */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (cfg) onEnabled?.(!!cfg.enabled); }, [cfg, onEnabled]);

  // persist the config row (model/provider/fail_mode/timeout/block_message/enabled)
  const saveCfg = async (patch, { silent } = {}) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSavingCfg(true);
    try {
      const r = await setLlmGuardrailConfig({
        enabled: next.enabled, model: next.model || '', provider: next.provider || '',
        fail_mode: next.fail_mode, block_message: next.block_message || '',
        timeout_seconds: next.timeout_seconds || 8,
      });
      setCfg(r.data.config);
      if (!silent) flash('AI guardrail saved');
    } catch (e) { flash(e.response?.data?.detail || 'Save failed', 'error'); load(); }
    finally { setSavingCfg(false); }
  };

  const toggleEnabled = () => {
    if (!cfg.enabled && (!cfg.model || !(cfg.policies || []).length)) {
      flash('Pick a judge model and add at least one policy first', 'error'); return;
    }
    saveCfg({ enabled: !cfg.enabled });
  };

  const pickModel = (id) => {
    const m = models.find((x) => x.id === id);
    saveCfg({ model: id, provider: m?.provider || '' }, { silent: true });
  };

  const addPolicy = async (p) => {
    const r = await addLlmGuardrailPolicy(p);
    setCfg(r.data.config); setAdding(false); flash('Policy added');
  };
  const removePolicy = async (id) => {
    const r = await deleteLlmGuardrailPolicy(id);
    setCfg(r.data.config); flash('Policy removed');
  };

  const runTest = async () => {
    if (!testText.trim()) return;
    setTesting(true); setTestResult(null);
    try {
      const r = await testLlmGuardrail({ text: testText });
      setTestResult(r.data);
    } catch (e) { setTestResult({ error: e.response?.data?.detail || 'Test failed' }); }
    finally { setTesting(false); }
  };

  if (loading || !cfg) return <div className="text-center text-fg-muted py-8 text-sm">Loading AI guardrail…</div>;

  const policies = cfg.policies || [];
  const provFor = (id) => (models.find((x) => x.id === id)?.provider) || '';
  const ready = cfg.model && policies.length > 0;

  return (
    <div>
      <p className="text-fg-muted text-sm mb-5 max-w-2xl">
        A model you choose reviews every request against your policies and blocks anything that violates them — runs after the regex layer, before the request reaches a provider. Adds one LLM call per request, so pick a fast, cheap judge.
      </p>

      {/* enforcement + model card */}
      <div className="bg-panel border border-panel-border rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${cfg.enabled ? 'bg-orange/10' : 'bg-field'}`}>
              <Bot className={`w-6 h-6 ${cfg.enabled ? 'text-orange' : 'text-fg-muted'}`} />
            </div>
            <div>
              <div className="text-fg font-semibold flex items-center gap-2">
                AI enforcement {cfg.enabled ? 'active' : 'paused'}
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cfg.enabled ? 'bg-green-500/10 text-green-400' : 'bg-field text-fg-muted'}`}>{cfg.enabled ? 'ON' : 'OFF'}</span>
              </div>
              <div className="text-fg-muted text-xs mt-0.5">
                {cfg.enabled ? `${policies.length} policy${policies.length === 1 ? '' : 'ies'} · judged by ${cfg.model || '—'}` : 'Requests are not reviewed by the AI layer.'}
              </div>
            </div>
          </div>
          <Toggle on={cfg.enabled} onClick={toggleEnabled} />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1.5">Judge model</label>
            <select value={cfg.model || ''} onChange={(e) => pickModel(e.target.value)}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm">
              <option value="">Select a model…</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>{m.id}{m.provider ? ` (${m.provider})` : ''}</option>
              ))}
            </select>
            {cfg.model && !provFor(cfg.model) && !cfg.provider &&
              <p className="text-[11px] text-amber-400 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />No provider mapped — the default provider will be used.</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1.5">On judge error / timeout</label>
            <select value={cfg.fail_mode} onChange={(e) => saveCfg({ fail_mode: e.target.value })}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm">
              <option value="open">Allow the request (fail-open)</option>
              <option value="closed">Block the request (fail-closed)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1.5">Timeout (seconds)</label>
            <input type="number" min={1} max={60} value={cfg.timeout_seconds}
              onChange={(e) => setCfg({ ...cfg, timeout_seconds: Number(e.target.value) })}
              onBlur={(e) => saveCfg({ timeout_seconds: Math.min(60, Math.max(1, Number(e.target.value) || 8)) }, { silent: true })}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1.5">Block message <span className="text-fg-muted/60">(shown to caller)</span></label>
            <input value={cfg.block_message || ''} onChange={(e) => setCfg({ ...cfg, block_message: e.target.value })}
              onBlur={(e) => saveCfg({ block_message: e.target.value }, { silent: true })}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm" placeholder="Your request was blocked by an AI safety policy." />
          </div>
        </div>
        {savingCfg && <div className="text-[11px] text-fg-muted mt-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Saving…</div>}
      </div>

      {/* policies */}
      <div className="bg-panel border border-panel-border rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Tag className="w-4 h-4 text-orange" /><span className="text-fg font-medium text-sm">Policies</span><span className="text-fg-muted text-xs">· {policies.length}</span></div>
          <button onClick={() => setAdding(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange hover:bg-orange-hover text-white text-sm font-medium rounded-lg transition-colors"><Plus className="w-4 h-4" />Add policy</button>
        </div>
        {policies.length === 0 ? (
          <p className="text-fg-muted text-sm py-4 text-center">No policies yet. Each policy is an instruction the judge enforces (e.g. “Block all PII”).</p>
        ) : (
          <div className="space-y-2">
            {policies.map((p) => (
              <div key={p.id} className="flex items-start gap-3 p-3 bg-field/40 border border-field-border rounded-xl">
                <div className="min-w-0 flex-1">
                  {p.label && <span className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange/10 text-orange mb-1">{p.label}</span>}
                  <p className="text-fg text-sm leading-relaxed break-words">{p.text}</p>
                </div>
                <button onClick={() => removePolicy(p.id)} title="Remove" className="p-1.5 text-fg-muted hover:text-red-400 rounded-lg hover:bg-field transition-colors shrink-0"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* test box */}
      <div className="bg-panel border border-panel-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><FlaskConical className="w-4 h-4 text-orange" /><span className="text-fg font-medium text-sm">Test the judge</span></div>
        <p className="text-fg-muted text-xs mb-2">Paste a sample message; this runs the current model + policies (no save needed) and shows the verdict.</p>
        <textarea value={testText} onChange={(e) => setTestText(e.target.value)} rows={3}
          className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm resize-y"
          placeholder="My SSN is 123-45-6789, can you store it?" />
        <div className="flex items-center gap-3 mt-3">
          <button onClick={runTest} disabled={testing || !ready || !testText.trim()}
            className="flex items-center gap-1.5 px-4 py-2 bg-orange hover:bg-orange-hover text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}Run test
          </button>
          {!ready && <span className="text-fg-muted text-xs">Select a model and add a policy to test.</span>}
        </div>
        {testResult && (
          <div className="mt-3">
            {testResult.error ? (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{testResult.error}</div>
            ) : testResult.parse_error ? (
              <div className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">{testResult.note} · {testResult.latency_ms}ms</div>
            ) : (
              <div className={`rounded-lg px-3 py-2.5 text-sm border ${testResult.verdict.block ? 'bg-red-500/10 border-red-500/20' : 'bg-green-500/10 border-green-500/20'}`}>
                <div className={`font-medium flex items-center gap-2 ${testResult.verdict.block ? 'text-red-400' : 'text-green-400'}`}>
                  {testResult.verdict.block ? 'BLOCKED' : 'ALLOWED'}
                  <span className="text-fg-muted text-xs font-normal">· {testResult.latency_ms}ms</span>
                </div>
                {testResult.verdict.block && (
                  <div className="text-fg-muted text-xs mt-1">
                    {testResult.verdict.policy && <>Policy: <span className="text-fg">{testResult.verdict.policy}</span> · </>}
                    {testResult.verdict.reason}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {adding && <AddPolicyModal onClose={() => setAdding(false)} onAdd={addPolicy} />}
    </div>
  );
}

// ── L3: PII guardrail (Presidio) ────────────────────────────────────────────
const PII_MODES = [
  { id: 'sanitize', label: 'Sanitize', desc: 'redact/mask & forward' },
  { id: 'block', label: 'Block', desc: 'reject the request' },
  { id: 'off', label: 'Off', desc: 'disabled' },
];

function AddPiiPatternModal({ operators, onClose, onAdd }) {
  const [f, setF] = useState({ entity: '', regex: '', score: 0.7, action: 'redact' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    if (!f.entity.trim()) { setError('Entity name is required'); return; }
    if (!f.regex.trim()) { setError('Regex is required'); return; }
    setBusy(true); setError(null);
    try { await onAdd({ ...f, entity: f.entity.trim().toUpperCase().replace(/\s+/g, '_') }); }
    catch (e) { setError(e.response?.data?.detail || 'Failed to add'); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-panel border border-panel-border rounded-2xl p-6 w-full max-w-lg shadow-2xl">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-fg">Add custom rule</h2>
          <button onClick={onClose} className="text-fg-muted hover:text-fg p-1 rounded-lg hover:bg-field"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-fg-muted mb-1.5">Entity name</label>
              <input value={f.entity} onChange={(e) => setF({ ...f, entity: e.target.value })}
                className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm" placeholder="EMP_ID" />
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-muted mb-1.5">Action</label>
              <select value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })}
                className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm">
                {operators.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1.5">Regex</label>
            <input value={f.regex} onChange={(e) => setF({ ...f, regex: e.target.value })}
              className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm font-mono" placeholder="\bEMP-\d{4}\b" />
          </div>
          <div>
            <label className="block text-xs font-medium text-fg-muted mb-1.5">Confidence score: {f.score}</label>
            <input type="range" min={0} max={1} step={0.05} value={f.score}
              onChange={(e) => setF({ ...f, score: Number(e.target.value) })} className="w-full accent-orange" />
          </div>
          {error && <p className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>}
        </div>
        <div className="flex gap-3 pt-5">
          <button onClick={submit} disabled={busy} className="flex-1 py-2.5 bg-orange hover:bg-orange-hover text-white font-medium rounded-lg text-sm disabled:opacity-50 transition-colors">{busy ? 'Adding…' : 'Add rule'}</button>
          <button onClick={onClose} className="px-6 py-2.5 bg-field border border-field-border text-fg rounded-lg hover:border-orange text-sm transition-colors">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// A curated library preset → a Layer 3 custom regex pattern.
function presetToPiiPattern(p) {
  const { tag, label } = parseName(p.savedName || p.name || '');
  const slug = String(label || 'RULE').toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  return { entity: (tag ? tag + '_' : '') + (slug || 'RULE'), regex: p.pattern, score: 0.85, action: 'redact' };
}

function PiiGuardrailSection({ flash, onEnabled }) {
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState({ available: true, ner_available: false, entity_catalog: [], operators: [], engines: ['off', 'spacy'] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [library, setLibrary] = useState(false);
  const [testText, setTestText] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getPiiGuardrail();
      setCfg(r.data.config);
      setMeta({ available: r.data.available, ner_available: r.data.ner_available, entity_catalog: r.data.entity_catalog || [], operators: r.data.operators || [], engines: r.data.engines || ['off', 'spacy'] });
    } catch { /* parent handles */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (cfg) onEnabled?.(!!cfg.enabled && cfg.mode !== 'off'); }, [cfg, onEnabled]);

  // PUT the whole config (entities, lists, scan, mode…)
  const persist = async (patch, { silent } = {}) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setSaving(true);
    try {
      const r = await setPiiGuardrailConfig({
        enabled: next.enabled, mode: next.mode, threshold: next.threshold, nlp_engine: next.nlp_engine,
        language: next.language || 'en', entities: next.entities || {}, deny_list: next.deny_list || [],
        allow_list: next.allow_list || [], scan_roles: next.scan_roles || ['user'],
        scan_tool_args: !!next.scan_tool_args, block_message: next.block_message || '',
      });
      setCfg(r.data.config);
      if (!silent) flash('PII guardrail saved');
    } catch (e) { flash(e.response?.data?.detail || 'Save failed', 'error'); load(); }
    finally { setSaving(false); }
  };

  const toggleEntity = (ent) => {
    const ents = { ...(cfg.entities || {}) };
    if (ents[ent]) delete ents[ent]; else ents[ent] = { action: 'redact' };
    persist({ entities: ents }, { silent: true });
  };
  const setEntityAction = (ent, action) => {
    const ents = { ...(cfg.entities || {}), [ent]: { ...(cfg.entities?.[ent] || {}), action } };
    persist({ entities: ents }, { silent: true });
  };
  const toggleRole = (role) => {
    const roles = new Set(cfg.scan_roles || ['user']);
    roles.has(role) ? roles.delete(role) : roles.add(role);
    persist({ scan_roles: [...roles] }, { silent: true });
  };

  const addPattern = async (p) => { const r = await addPiiPattern(p); setCfg(r.data.config); setAdding(false); flash('Rule added'); };
  const removePattern = async (id) => { const r = await deletePiiPattern(id); setCfg(r.data.config); flash('Rule removed'); };
  const addLibraryPreset = async (p) => { await addPiiPattern(presetToPiiPattern(p)); };

  const runTest = async () => {
    if (!testText.trim()) return;
    setTesting(true); setTestResult(null);
    try { setTestResult((await testPiiGuardrail({ text: testText })).data); }
    catch (e) { setTestResult({ error: e.response?.data?.detail || 'Test failed' }); }
    finally { setTesting(false); }
  };

  if (loading || !cfg) return <div className="text-center text-fg-muted py-8 text-sm">Loading PII guardrail…</div>;
  const entities = cfg.entities || {};
  const patterns = cfg.custom_patterns || [];

  return (
    <div>
      <p className="text-fg-muted text-sm mb-4 max-w-2xl">
        Detects sensitive data (cards, SSN, Aadhaar, PAN, email…) by pattern and checksum — no model, near-zero cost — then blocks the request or rewrites it (redact/mask) and forwards the clean version.
      </p>

      {!meta.available && (
        <div className="mb-4 px-4 py-3 rounded-lg text-sm bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />Presidio isn’t installed on this deployment — add <code className="font-mono">presidio-analyzer</code> &amp; <code className="font-mono">presidio-anonymizer</code> to enable this layer.
        </div>
      )}

      {/* mode + master card */}
      <div className="bg-panel border border-panel-border rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${cfg.enabled && cfg.mode !== 'off' ? 'bg-orange/10' : 'bg-field'}`}>
              <ScanSearch className={`w-6 h-6 ${cfg.enabled && cfg.mode !== 'off' ? 'text-orange' : 'text-fg-muted'}`} />
            </div>
            <div>
              <div className="text-fg font-semibold flex items-center gap-2">
                PII enforcement {cfg.enabled && cfg.mode !== 'off' ? 'active' : 'paused'}
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cfg.enabled && cfg.mode !== 'off' ? 'bg-green-500/10 text-green-400' : 'bg-field text-fg-muted'}`}>{cfg.enabled && cfg.mode !== 'off' ? 'ON' : 'OFF'}</span>
              </div>
              <div className="text-fg-muted text-xs mt-0.5">{Object.keys(entities).length} entities watched · mode: {cfg.mode}</div>
            </div>
          </div>
          <Toggle on={cfg.enabled} onClick={() => persist({ enabled: !cfg.enabled })} />
        </div>

        {/* mode segmented control */}
        <div className="grid grid-cols-3 gap-2">
          {PII_MODES.map((m) => (
            <button key={m.id} onClick={() => persist({ mode: m.id }, { silent: true })}
              className={`px-2 py-2 rounded-lg border text-center transition-colors ${cfg.mode === m.id ? 'border-orange bg-orange/[0.06]' : 'border-field-border bg-field hover:border-orange/50'}`}>
              <div className={`text-sm font-medium ${cfg.mode === m.id ? 'text-orange' : 'text-fg'}`}>{m.label}</div>
              <div className="text-[10px] text-fg-muted mt-0.5">{m.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* entities table */}
      <div className="bg-panel border border-panel-border rounded-2xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><Tag className="w-4 h-4 text-orange" /><span className="text-fg font-medium text-sm">Entities &amp; actions</span></div>
          <div className="flex items-center gap-2">
            <button onClick={() => setLibrary(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-orange hover:bg-orange-hover text-white text-sm font-medium rounded-lg transition-colors"><LibraryBig className="w-4 h-4" />Browse library</button>
            <button onClick={() => setAdding(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-field border border-field-border text-fg hover:border-orange text-sm rounded-lg transition-colors"><Plus className="w-4 h-4" />Custom rule</button>
          </div>
        </div>
        {meta.entity_catalog.map((grp) => (
          <div key={grp.group} className="mb-3 last:mb-0">
            <div className="text-[11px] font-semibold text-fg-muted uppercase tracking-wide mb-1.5">{grp.group}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {grp.entities.map((ent) => {
                const on = !!entities[ent];
                return (
                  <div key={ent} className={`flex items-center gap-2 p-2 rounded-lg border ${on ? 'border-orange/40 bg-orange/[0.04]' : 'border-field-border'}`}>
                    <button onClick={() => toggleEntity(ent)} className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-orange border-orange' : 'border-field-border'}`}>
                      {on && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                    </button>
                    <span className="text-fg text-sm font-mono flex-1 truncate">{ent}</span>
                    {on && (
                      <select value={entities[ent].action} onChange={(e) => setEntityAction(ent, e.target.value)}
                        className="px-2 py-1 bg-field border border-field-border rounded text-fg text-xs">
                        {meta.operators.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {/* custom patterns */}
        {patterns.length > 0 && (
          <div className="mt-3 pt-3 border-t border-panel-border">
            <div className="text-[11px] font-semibold text-fg-muted uppercase tracking-wide mb-1.5">Custom rules</div>
            <div className="space-y-1.5">
              {patterns.map((p) => (
                <div key={p.id} className="flex items-center gap-2 p-2 bg-field/40 border border-field-border rounded-lg">
                  <span className="text-fg text-sm font-mono flex-1 truncate">{p.entity}</span>
                  <select value={(entities[p.entity] && entities[p.entity].action) || p.action}
                    onChange={(e) => setEntityAction(p.entity, e.target.value)}
                    className="px-2 py-1 bg-field border border-field-border rounded text-fg text-xs">
                    {meta.operators.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <button onClick={() => removePattern(p.id)} className="p-1 text-fg-muted hover:text-red-400 rounded"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* engine + scope + lists */}
      <div className="bg-panel border border-panel-border rounded-2xl p-5 mb-4 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-fg-muted mb-1.5">Detection engine</label>
          <select value={cfg.nlp_engine} onChange={(e) => persist({ nlp_engine: e.target.value }, { silent: true })}
            className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm">
            <option value="off">Regex + checksum only (no model, free)</option>
            <option value="spacy">Local NER model (adds names/places)</option>
          </select>
          {cfg.nlp_engine === 'spacy' && (meta.ner_available
            ? <p className="text-[11px] text-green-400 mt-1 flex items-center gap-1"><Check className="w-3 h-3" />NER model loaded — names &amp; places will be detected.</p>
            : <p className="text-[11px] text-amber-400 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />spaCy model not installed on this deployment — names/places are skipped (regex still runs). Bake <code className="font-mono">en_core_web_lg</code> into the image.</p>)}
        </div>
        <div>
          <label className="block text-xs font-medium text-fg-muted mb-1.5">Min confidence: {cfg.threshold}</label>
          <input type="range" min={0} max={1} step={0.05} value={cfg.threshold}
            onChange={(e) => setCfg({ ...cfg, threshold: Number(e.target.value) })}
            onMouseUp={(e) => persist({ threshold: Number(e.target.value) }, { silent: true })} className="w-full accent-orange mt-2" />
        </div>
        <div>
          <label className="block text-xs font-medium text-fg-muted mb-1.5">Scan roles</label>
          <div className="flex gap-3">
            {['user', 'assistant', 'system'].map((r) => (
              <label key={r} className="flex items-center gap-1.5 text-sm text-fg cursor-pointer">
                <input type="checkbox" checked={(cfg.scan_roles || ['user']).includes(r)} onChange={() => toggleRole(r)} className="accent-orange w-4 h-4" />{r}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-fg-muted mb-1.5">Block message <span className="text-fg-muted/60">(block mode)</span></label>
          <input value={cfg.block_message || ''} onChange={(e) => setCfg({ ...cfg, block_message: e.target.value })}
            onBlur={(e) => persist({ block_message: e.target.value }, { silent: true })}
            className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-fg-muted mb-1.5">Deny-list <span className="text-fg-muted/60">(always flag, comma-sep)</span></label>
          <input value={(cfg.deny_list || []).join(', ')} onChange={(e) => setCfg({ ...cfg, deny_list: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            onBlur={() => persist({ deny_list: cfg.deny_list || [] }, { silent: true })}
            className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm" placeholder="Project Falcon, codename-x" />
        </div>
        <div>
          <label className="block text-xs font-medium text-fg-muted mb-1.5">Allow-list <span className="text-fg-muted/60">(never flag, comma-sep)</span></label>
          <input value={(cfg.allow_list || []).join(', ')} onChange={(e) => setCfg({ ...cfg, allow_list: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
            onBlur={() => persist({ allow_list: cfg.allow_list || [] }, { silent: true })}
            className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm" placeholder="Apple, Washington" />
        </div>
        {saving && <div className="text-[11px] text-fg-muted flex items-center gap-1 sm:col-span-2"><Loader2 className="w-3 h-3 animate-spin" />Saving…</div>}
      </div>

      {/* test box */}
      <div className="bg-panel border border-panel-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><FlaskConical className="w-4 h-4 text-orange" /><span className="text-fg font-medium text-sm">Test detection</span></div>
        <textarea value={testText} onChange={(e) => setTestText(e.target.value)} rows={3}
          className="w-full px-3 py-2 bg-field border border-field-border rounded-lg text-fg text-sm resize-y"
          placeholder="My card is 4111 1111 1111 1111 and PAN ABCDE1234F" />
        <button onClick={runTest} disabled={testing || !meta.available || !testText.trim()}
          className="mt-3 flex items-center gap-1.5 px-4 py-2 bg-orange hover:bg-orange-hover text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}Run test
        </button>
        {testResult && (
          <div className="mt-3">
            {testResult.error ? (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{testResult.error}</div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-fg-muted">Found {testResult.found.length} · {testResult.latency_ms}ms</div>
                <div className="flex flex-wrap gap-1.5">
                  {testResult.found.map((f, i) => (
                    <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-orange/10 text-orange font-mono">{f.entity} · {f.score}</span>
                  ))}
                </div>
                <div className="text-sm text-fg bg-field/50 border border-field-border rounded-lg px-3 py-2 font-mono break-words">{testResult.redacted}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {adding && <AddPiiPatternModal operators={meta.operators} onClose={() => setAdding(false)} onAdd={addPattern} />}
      {library && (
        <LibraryModal
          existingPatterns={new Set(patterns.map((p) => p.regex))}
          onClose={() => setLibrary(false)}
          onAdd={addLibraryPreset}
          onBulkAdded={(n) => { setLibrary(false); load(); flash(`Added ${n} from library`); }} />
      )}
    </div>
  );
}

export default function Guardrails() {
  const [msg, setMsg] = useState(null);
  const [tab, setTab] = useState('l2');              // 'l2' (AI judge) | 'l3' (PII)
  const [l2Enabled, setL2Enabled] = useState(false); // mirrored from L2 section for the tab badge
  const [l3Enabled, setL3Enabled] = useState(false); // mirrored from L3 section for the tab badge

  const flash = (text, type = 'success') => { setMsg({ type, text }); setTimeout(() => setMsg(null), 3000); };

  const TabBtn = ({ id, icon: Icon, label, on }) => (
    <button onClick={() => setTab(id)}
      className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors border ${
        tab === id ? 'bg-panel border-panel-border text-fg' : 'bg-transparent border-transparent text-fg-muted hover:text-fg hover:bg-field/40'}`}>
      <Icon className={`w-4 h-4 ${tab === id ? 'text-orange' : ''}`} />
      <span>{label}</span>
      <span className={`ml-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${on ? 'bg-green-500/10 text-green-400' : 'bg-field text-fg-muted'}`}>{on ? 'ON' : 'OFF'}</span>
    </button>
  );

  return (
    <div className="w-full">
      {/* header */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-fg flex items-center gap-2"><Shield className="w-6 h-6 text-orange" />Guardrails</h1>
        <p className="text-fg-muted text-sm mt-1 max-w-2xl">Two independent layers screen every request before it reaches a model. Enable either or both.</p>
      </div>

      {/* tab bar */}
      <div className="flex items-center gap-2 mb-5 border-b border-panel-border pb-3">
        <TabBtn id="l2" icon={Bot} label="Layer 1" on={l2Enabled} />
        <TabBtn id="l3" icon={ScanSearch} label="Layer 2" on={l3Enabled} />
      </div>

      {msg && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm flex justify-between items-center ${msg.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
          <span>{msg.text}</span><button onClick={() => setMsg(null)} className="hover:opacity-70"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ── L2 tab ── */}
      {tab === 'l2' && <LlmGuardrailSection flash={flash} onEnabled={setL2Enabled} />}

      {/* ── L3 tab ── */}
      {tab === 'l3' && <PiiGuardrailSection flash={flash} onEnabled={setL3Enabled} />}
    </div>
  );
}
